import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '../fetchBaseQuery';
import type { File } from './types';

export * from './types';

type GetFileStatsResponse = {
    id: number;
    result: string;
    stats: {
        language_id: number;
        iso: string;
        flag: string;
        percent: number;
        class: string;
        language_name: string;
    }[];
};

type GetFilesResponse = {
    data: File[];
};

export type ProgressInformation = {
    id: number;
    iso: string;
    percent: number;
    name: string;
    isBase: boolean;
};

type GetProjectFileProgressResponse = {
    data: ProgressInformation[];
};

interface RenameFileResponseSuccess {
    err: 0;
    id: number;
    filename: string;
}

interface RenameFileResponseError {
    err: 1;
    message: string;
}

type RenameFileResponse = RenameFileResponseSuccess | RenameFileResponseError;

const projectFilesApi = createApi({
    reducerPath: 'filenameApi',
    baseQuery: fetchBaseQuery(),
    tagTypes: [],
    refetchOnMountOrArgChange: true,
    endpoints: (builder) => ({
        getProjectFiles: builder.query<GetFilesResponse, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/projects/${projectId}/files/`,
            }),
        }),
        getProjectFileProgress: builder.query<GetProjectFileProgressResponse, { projectId: string; fileId: number }>({
            query: ({ projectId, fileId }) => ({
                method: 'GET',
                url: `/projects/${projectId}/files/${fileId}/progress`,
            }),
        }),
        getProjectFileStats: builder.query<GetFileStatsResponse, number>({
            query: (id) => ({
                method: 'POST',
                url: `/filenames/${id}/stats`,
            }),
        }),
        renameProjectFile: builder.mutation<RenameFileResponse, { fileId: number; newName: string }>({
            query: ({ fileId, newName }) => {
                const formData = new FormData();
                formData.append('filename', newName);

                return {
                    method: 'POST',
                    url: `/filenames/${fileId}/rename`,
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
        }),
    }),
});

export const {
    useGetProjectFileStatsQuery,
    useLazyGetProjectFileProgressQuery,
    useGetProjectFilesQuery,
    useRenameProjectFileMutation,
} = projectFilesApi;

export default projectFilesApi;
